import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { iTopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesEndpoint } from '@/api/api.generated';
import type { 
  ITopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesRequest,
  ITopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesResponse,
  ITopFiveApiV2EndpointsTopFiveCalculateBonusesStaffTopFiveBonusItem
} from '@/api/models';

export type StaffTopFiveBonusItem = ITopFiveApiV2EndpointsTopFiveCalculateBonusesStaffTopFiveBonusItem;
export type CalculateBonusesResponse = ITopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesResponse;

export interface CalculateProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Custom hook for calculating TopFive bonuses from CSV file.
 * Uses generated API client from OpenAPI spec.
 * 
 * Features:
 * - Multipart form data upload via generated API client
 * - Real-time upload progress tracking
 * - Error handling with detailed error information
 * - CSV download functionality for results
 * - TypeScript type safety with generated types
 * 
 * @example
 * ```tsx
 * const { calculateBonuses, isCalculating, progress, error } = useCalculateBonuses();
 * 
 * const handleSubmit = async (file: File, startDate: string, endDate: string) => {
 *   const result = await calculateBonuses(file, startDate, endDate);
 *   if (result) {
 *     console.log('Calculation successful!', result.staffTopFiveBonuses);
 *   }
 * };
 * ```
 */
export const useCalculateBonuses = () => {
  const [progress, setProgress] = useState<CalculateProgress>({ loaded: 0, total: 0, percentage: 0 });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ file, startDate, endDate }: { file: File; startDate: string; endDate: string }) => {
      setError(null);
      setProgress({ loaded: 0, total: 0, percentage: 0 });

      const request: ITopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesRequest = {
        file: file,
        startDate: startDate,
        endDate: endDate,
      };

      const response = await iTopFiveApiV2EndpointsTopFiveCalculateBonusesCalculateBonusesEndpoint(
        request,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            if (total) {
              const percentage = Math.round((loaded * 100) / total);
              setProgress({ loaded, total, percentage });
            }
          },
        }
      );

      return response.data as CalculateBonusesResponse;
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setError('You do not have permission to calculate bonuses. Please contact your administrator.');
      } else {
        setError(err.message || 'Network error. Please check your connection.');
      }
    },
  });

  const calculateBonuses = async (
    file: File,
    startDate: string,
    endDate: string
  ): Promise<CalculateBonusesResponse | null> => {
    try {
      const result = await mutation.mutateAsync({ file, startDate, endDate });
      return result;
    } catch (err) {
      // Error already handled in onError
      return null;
    }
  };

  const resetState = () => {
    mutation.reset();
    setProgress({ loaded: 0, total: 0, percentage: 0 });
    setError(null);
  };

  /**
   * Downloads the calculation results as a CSV file
   */
  const downloadResultsAsCsv = (bonuses: StaffTopFiveBonusItem[], filename: string = 'TopFiveBonuses.csv') => {
    if (!bonuses || bonuses.length === 0) {
      return;
    }

    // Convert to CSV
    const headers = ['FirstName', 'Surname', 'EmployeeId', 'Department', 'Name', 'Weight', 'Result', 'Amount', 'ExecutiveName'];
    const rows = bonuses.map(b => [
      b.firstName || '',
      b.surname || '',
      b.employeeId || '',
      b.department || '',
      b.name || '',
      b.weight?.toString() || '0',
      b.result || '',
      b.amount?.toString() || '0',
      b.executiveName || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    calculateBonuses,
    downloadResultsAsCsv,
    isCalculating: mutation.isPending,
    progress,
    error,
    resetState,
  };
};
